import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const InventoryCard = ({ item, onViewDetails }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'great':
      case 'perfect':
        return 'bg-emerald-500 text-white border-transparent shadow-sm';
      case 'good':
        return 'bg-blue-500 text-white border-transparent shadow-sm';
      case 'damaged':
        return 'bg-red-500 text-white border-transparent shadow-sm';
      default:
        return 'bg-secondary text-secondary-foreground border-transparent shadow-sm';
    }
  };

  const getDeliveryStatusColor = (deliveryStatus) => {
    switch (deliveryStatus?.toLowerCase()) {
      case 'in_warehouse':
        return 'bg-blue-600 text-white border-transparent shadow-sm';
      case 'in_route':
        return 'bg-orange-500 text-white border-transparent shadow-sm';
      case 'delivered':
        return 'bg-emerald-600 text-white border-transparent shadow-sm';
      default:
        return 'bg-secondary text-secondary-foreground border-transparent shadow-sm';
    }
  };

  const getDeliveryStatusLabel = (deliveryStatus) => {
    switch (deliveryStatus?.toLowerCase()) {
      case 'in_warehouse': return 'In Warehouse';
      case 'in_route': return 'In Route';
      case 'delivered': return 'Delivered';
      default: return deliveryStatus;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div
      className="bg-card rounded-lg border border-border overflow-hidden elevation-1 transition-all hover:elevation-2 hover:border-primary/40 hover:-translate-y-0.5 cursor-pointer group"
      onClick={() => onViewDetails?.(item)}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${item?.name}`}
      onKeyDown={(e) => e.key === 'Enter' && onViewDetails?.(item)}
    >
      <div className="relative h-48 bg-muted overflow-hidden">
        <Image
          src={item?.primaryImage}
          alt={item?.primaryImageAlt}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />

        {/* Condition badge */}
        <div className="absolute top-3 right-3 z-10">
          <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${getStatusColor(item?.status)}`}>
            {item?.status}
          </span>
        </div>

        {/* Delivery status badge */}
        <div className="absolute top-11 right-3 z-10">
          <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${getDeliveryStatusColor(item?.deliveryStatus)}`}>
            {getDeliveryStatusLabel(item?.deliveryStatus)}
          </span>
        </div>

        {/* Photo count badge */}
        {item?.imageCount > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/65 text-white px-2 py-1 rounded-md flex items-center gap-1 backdrop-blur-sm">
            <Icon name="Images" size={13} />
            <span className="text-xs font-medium">{item?.imageCount} photos</span>
          </div>
        )}

        {/* "View Details" hover overlay */}
        <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/10 transition-colors duration-200 flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-background/90 backdrop-blur-sm text-foreground text-xs font-semibold px-3 py-1.5 rounded-full border border-border shadow-sm flex items-center gap-1.5">
            <Icon name="Expand" size={13} />
            View Details
          </span>
        </div>
      </div>

      <div className="p-4">
        <div className="mb-3">
          <h3 className="text-base font-semibold text-foreground mb-0.5 line-clamp-1 group-hover:text-primary transition-colors">
            {item?.name}
          </h3>
          <p className="text-xs text-muted-foreground font-mono">LIIN: {item?.liin}</p>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <Icon name="Calendar" size={14} color="var(--color-muted-foreground)" />
            <span className="text-xs text-muted-foreground">Arrived: {formatDate(item?.arrivalDate)}</span>
          </div>

          {item?.location && (
            <div className="flex items-center gap-2">
              <Icon name="MapPin" size={14} color="var(--color-muted-foreground)" />
              <span className="text-xs text-muted-foreground line-clamp-1">{item?.location}</span>
            </div>
          )}

          {item?.notes && (
            <div className="flex items-start gap-2">
              <Icon name="FileText" size={14} color="var(--color-muted-foreground)" className="mt-0.5 flex-shrink-0" />
              <p className="text-xs text-muted-foreground line-clamp-2">{item?.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryCard;